import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import LoadingPage from '@/components/ui/LoadingPage';
import { useJawsScanSummary } from '@/hooks/useJawsScan';
import { useJawsSegmentation } from '@hooks/useJawsSegmentation';
import { useJawsCorrections } from '@/hooks/useJawsCorrection.ts';
import type { JawsSegmentationResponse } from '@/schema/jawsSegmentation';
import type { JawsCorrectionResponse } from '@/schema/JawsCorrection';
import { ExportCanvas } from '@export/ExportCanvas';
import { ExportUI } from '@export/UI/ExportUI';
import useExportStore from '@export/hooks/useExportStore.ts';

interface ExportContentProps {
  segmentation: JawsSegmentationResponse;
  corrections: JawsCorrectionResponse[];
}

const ExportContent = ({ segmentation, corrections }: ExportContentProps) => {
  const { activeCorrectionId, setCorrections, setActiveCorrectionId } = useExportStore();

  useEffect(() => {
    setCorrections(corrections);
  }, [corrections, setCorrections]);

  useEffect(() => {
    if (!corrections.length) {
      setActiveCorrectionId(null);
      return;
    }
    const isActiveStillValid = corrections.some((correction) => correction.id === activeCorrectionId);
    if (!isActiveStillValid) {
      setActiveCorrectionId(corrections[0].id);
    }
  }, [corrections, activeCorrectionId, setActiveCorrectionId]);

  const activeCorrection = useMemo(
    () => corrections.find((correction) => correction.id === activeCorrectionId) ?? corrections[0] ?? null,
    [corrections, activeCorrectionId],
  );

  return (
    <div className="relative h-full w-full">
      <ExportCanvas segmentation={segmentation} activeCorrection={activeCorrection} />
      <ExportUI corrections={corrections} />
    </div>
  );
};

export default function Export() {
  useDocumentTitle('Braces Designer - Export | fullName');
  const { scanId } = useParams<{ patientId: string; scanId: string }>();
  const { setScanId, reset } = useExportStore();

  useEffect(() => {
    setScanId(scanId ?? null);
    return () => {
      reset();
    };
  }, [scanId, setScanId, reset]);

  if (!scanId) {
    return <div className="p-6 text-white">Invalid scan reference.</div>;
  }

  const scanSummaryQuery = useJawsScanSummary(scanId, { enabled: Boolean(scanId) });
  const segmentationId = scanSummaryQuery.data?.jawsSegmentationId ?? null;
  const correctionIds = scanSummaryQuery.data?.jawsCorrectionIds ?? [];

  const segmentationQuery = useJawsSegmentation(segmentationId ?? '', { enabled: Boolean(segmentationId) });
  const correctionsQuery = useJawsCorrections(correctionIds, { enabled: correctionIds.length > 0 });

  if (
    scanSummaryQuery.isLoading ||
    (segmentationId && segmentationQuery.isLoading) ||
    (correctionIds.length > 0 && correctionsQuery.isLoading)
  ) {
    return <LoadingPage loadingText="Loading export workspace..." />;
  }

  if (scanSummaryQuery.isError || !scanSummaryQuery.data) {
    return <div className="p-6 text-white">Error loading scan data.</div>;
  }

  if (!segmentationId) {
    return <div className="p-6 text-white">Segmentation must be completed before exporting corrections.</div>;
  }

  if (segmentationQuery.isError || !segmentationQuery.data) {
    return <div className="p-6 text-white">Error loading segmentation data.</div>;
  }

  if (!correctionIds.length) {
    return <div className="p-6 text-white">No corrections available for this scan yet.</div>;
  }

  if (correctionsQuery.isError || !correctionsQuery.data) {
    return <div className="p-6 text-white">Error loading corrections data.</div>;
  }

  return <ExportContent segmentation={segmentationQuery.data} corrections={correctionsQuery.data} />;
}

